import { useMemo } from "react";
import * as v from "valibot";
import { create } from "zustand";

interface Preferences {
  /** **簡単モード** `true`の時、表示が簡略化される。 */
  easy: boolean;
  /** 音声合成の速度 */
  textToSpeechRate: number;
  /** 音声合成の音量 */
  textToSpeechVolume: number;
  /** 音量[0.0, 1.0] */
  volume: number;

  setEasy(easy: boolean): void;
  setTextToSpeechRate(rate: number): void;
  setTextToSpeechVolume(volume: number): void;
  setVolume(volume: number): void;
}

const usePreferences = create<Preferences>(function (set) {
  const easy = window.localStorage.getItem("Reha30.Preferences.easy")?.toLowerCase() !== "false";

  let textToSpeechRate;
  try {
    textToSpeechRate = v.parse(
      v.nullable(
        v.pipe(
          v.union([v.number(), v.pipe(v.string(), v.toNumber())]),
          v.minValue(0.1),
          v.maxValue(10.0),
        ),
        1.0,
      ),
      window.localStorage.getItem("Reha30.Preferences.textToSpeechRate"),
    );
  } catch (error) {
    console.error(error);
    textToSpeechRate = 1.0;
    window.localStorage.setItem("Reha30.Preferences.textToSpeechRate", String(1.0));
  }

  let textToSpeechVolume;
  try {
    textToSpeechVolume = v.parse(
      v.nullable(
        v.pipe(
          v.union([v.number(), v.pipe(v.string(), v.toNumber())]),
          v.minValue(0.0),
          v.maxValue(1.0),
        ),
        1.0,
      ),
      window.localStorage.getItem("Reha30.Preferences.textToSpeechVolume"),
    );
  } catch (error) {
    console.error(error);
    textToSpeechVolume = 1.0;
    window.localStorage.setItem("Reha30.Preferences.textToSpeechVolume", String(1.0));
  }

  let volume;
  try {
    volume = v.parse(
      v.nullable(
        v.pipe(
          v.union([v.number(), v.pipe(v.string(), v.toNumber())]),
          v.minValue(0.0),
          v.maxValue(1.0),
        ),
        1.0,
      ),
      window.localStorage.getItem("Reha30.Preferences.volume"),
    );
  } catch (error) {
    console.error(error);
    volume = 1.0;
    window.localStorage.setItem("Reha30.Preferences.volume", String(1.0));
  }

  return {
    easy,
    textToSpeechRate,
    textToSpeechVolume,
    volume,

    setEasy(easy: boolean) {
      set({ easy });
      window.localStorage.setItem("Reha30.Preferences.easy", String(easy));
    },

    setTextToSpeechRate(rate: number) {
      set({ textToSpeechRate: rate });
      window.localStorage.setItem("Reha30.Preferences.textToSpeechRate", String(rate));
    },

    setTextToSpeechVolume(volume: number) {
      set({ textToSpeechVolume: volume });
      window.localStorage.setItem("Reha30.Preferences.textToSpeechVolume", String(volume));
    },

    setVolume(volume: number) {
      set({ volume });
      window.localStorage.setItem("Reha30.Preferences.volume", String(volume));
    },
  };
});

export function useEasy() {
  const easy = usePreferences(function ({ easy }) {
    return easy;
  });

  return useMemo(
    function () {
      return [easy, usePreferences.getState().setEasy] as const;
    },
    [easy],
  );
}

export function useTextToSpeechRate() {
  const textToSpeechRate = usePreferences(function ({ textToSpeechRate }) {
    return textToSpeechRate;
  });

  return useMemo(
    function () {
      return [textToSpeechRate, usePreferences.getState().setTextToSpeechRate] as const;
    },
    [textToSpeechRate],
  );
}

export function useTextToSpeechVolume() {
  const textToSpeechVolume = usePreferences(function ({ textToSpeechVolume }) {
    return textToSpeechVolume;
  });

  return useMemo(
    function () {
      return [textToSpeechVolume, usePreferences.getState().setTextToSpeechVolume] as const;
    },
    [textToSpeechVolume],
  );
}

export function useVolume() {
  const volume = usePreferences(function ({ volume }) {
    return volume;
  });

  return useMemo(
    function () {
      return [volume, usePreferences.getState().setVolume] as const;
    },
    [volume],
  );
}
